import {AuthService} from "./AuthService";
import moment from "moment";
import {marketplaceListParams} from "../components/Product/marketplace/marketplaceProfile";

// Pure payload builders for the supplier row-action endpoints (task-19-report.md's
// contract table). Exported -- and used by the service methods below -- so their
// exact key names can be asserted directly in supplierActions.test.js without
// mounting the Suppliers tab or mocking AuthService. Two of them deliberately
// keep their competitor twins' key names (`competitor_url`, `product_url`)
// rather than `supplier_url`: the underlying pricerest service functions are
// shared, not duplicated, so the field names were never renamed.
export function buildAddSupplierPayload(product_url) {
    return {suppliers: [{product_url: product_url}]};
}

// Shared shape for suppliers/delete, suppliers/activate and suppliers/deactivate.
export function buildSupplierProductUrlsPayload(product_urls) {
    return {product_urls: product_urls};
}

export function buildUpdateSupplierFieldsPayload(data) {
    return {
        competitor_url: data.competitor_url,
        product_url: data.product_url,
        multiplier: data.multiplier,
        discount_type: data.discount_type
    };
}

export function buildAddSupplierPricePayload(data) {
    return {
        competitor_url: data.competitor_url,
        added_price: data.added_price,
        added_sale_price: data.added_sale_price,
        added_currency: data.added_currency,
        added_stock: data.added_stock,
        added_date: data.added_date
    };
}

export function buildDeleteSupplierPricePayload(competitor_url, created_at) {
    return {competitor_url: competitor_url, created_at: created_at};
}

// suppliers/check_price takes no request body -- it always creates a fresh,
// product-scoped scraping request server-side (Task 20 backend fix:
// pricerest product/views.py:check_supplier_price /
// scraper/service.py:create_supplier_check_scraping_request). Kept as an
// explicit builder, not a bare {} at the call site, so "this endpoint takes
// no body" is asserted in supplierActions.test.js the same way the others are.
export function buildCheckSupplierPricePayload() {
    return {};
}

// The supplier status filter as request params: supplier_status_websites (a
// comma-separated host list) and supplier_status (no_match and/or no_stock).
// Left out entirely while either is empty, so every request without the
// filter carries exactly the params it carried before.
export function supplierStatusListParams(supplierFilters = {}) {
    const websites = supplierFilters.supplier_status_websites || '';
    const statuses = supplierFilters.supplier_status || '';
    if (!websites || !statuses)
        return {};
    return {supplier_status_websites: websites, supplier_status: statuses};
}

export class ProductService {
    url = `products/`;
    fields_url = `fields/`;

    // The product list's filters as request params, shared by a page and the count.
    // import_url narrows the list down to the products a single category-page
    // import link brought in (the campaign setup screen links here).
    // supplierFilters carries the product list's supplier filter group, which
    // the server has to apply: has_supplier / has_min_value are 'true'/'false'
    // ('' when off) and supplier_websites is a comma-separated host list.
    // The supplier status filter (supplierStatusListParams) is sent only while set.
    // The same object carries the marketplace operator filters (mp_cheaper_on,
    // mp_min_diff_pct, mp_first_party, mp_status, mp_buybox), also applied by the server. Only the ones
    // that are set are sent, so other profiles' requests are unchanged.
    productListParams(category, brand, is_active, name, campaign_id, import_url = '', supplierFilters = {}) {
        return {
            product_id: '',
            category: category,
            brand: brand,
            is_active: is_active,
            name: name,
            campaign_id: campaign_id,
            import_url: import_url,
            has_supplier: supplierFilters.has_supplier || '',
            supplier_websites: supplierFilters.supplier_websites || '',
            has_min_value: supplierFilters.has_min_value || '',
            ...supplierStatusListParams(supplierFilters),
            ...marketplaceListParams(supplierFilters)};
    }

    // One page of the product list as {products, total}. With includeTotal the
    // first page (start_index 0) carries the list's size; the panel asks
    // getProductCount alongside instead, so that page does not wait on it.
    getProducts(category, brand, is_active, name, campaign_id, start_index, import_url = '',
                supplierFilters = {}, includeTotal = true) {

        return AuthService.apiGet(this.url, {
            params: {
                ...this.productListParams(category, brand, is_active, name, campaign_id, import_url, supplierFilters),
                start_index: start_index,
                include_total: includeTotal ? 'true' : 'false'}
        }).then(res => ({products: res.data.products, total: res.data.total}));
    }

    // The product list's size for the same filters as getProducts.
    getProductCount(category, brand, is_active, name, campaign_id, import_url = '', supplierFilters = {}) {
        return AuthService.apiGet(this.url + "count/", {
            params: this.productListParams(category, brand, is_active, name, campaign_id, import_url, supplierFilters)
        }).then(res => res.data.total);
    }

    // The campaign's supplier hosts, for the supplier filter group's site
    // dropdown. Read campaign-wide rather than collected from the loaded list:
    // the supplier-site filter runs server-side, so a list already narrowed to
    // one host could only rebuild a one-entry dropdown.
    getSupplierWebsites(campaign_id) {
        return AuthService.apiGet(this.url + "supplier_websites/", {
            params: {campaign_id: campaign_id}
        }).then(res => res.data.supplier_websites);
    }

    // {import_url: active product count} for a campaign's import links.
    getImportUrlCounts(campaign_id) {
        return AuthService.apiGet(this.url + "import_url_counts/", {
            params: {campaign_id: campaign_id}
        }).then(res => res.data.import_url_counts);
    }

    getProduct(product_id) {
        return AuthService.apiGet(this.url + product_id + "/").then(res => res.data.product).catch(error=> {throw error});
    }

    getCompetitors(product_id, history_start_date, history_end_date){
        return AuthService.apiGet(this.url + product_id + "/competitors/", {
            params: {
                history_start_date: moment(history_start_date).startOf('day').toDate(),
                history_end_date: moment(history_end_date).endOf('day').toDate()}
        }).then(res => res.data.competitors).catch(error=> {throw error});
    }


    addProduct(product) {
        return AuthService.apiPost(this.url, {"product": product}).then(res => res.data.product_created);
    }

    updateProduct(id, product) {
        return AuthService.apiPut(this.url + id + "/", product).then(res => res.data.product_updated);
    }

    bulkUpdateProducts(data) {
        return AuthService.apiPut(this.url + "update_bulk/", data).then(res => res.data.message);
    }

    // Single-product write path for the supplier margin override, riding the
    // same update_bulk endpoint the bulk-edit screen uses (there is no
    // per-field endpoint). null clears the override back to the campaign's
    // margin; 0 is a real override (sell at supplier cost) -- both must be
    // sent explicitly, never omitted. The other update_bulk fields are sent as
    // no-ops (falsy / empty) so this call only ever touches the margin.
    updateSupplierMarginRate(campaign_id, product_id, supplier_margin_rate) {
        return AuthService.apiPut(this.url + "update_bulk/", {
            campaign_id: campaign_id,
            product_ids: [product_id],
            brand: "",
            category: "",
            sub_category: null,
            type: null,
            tags: [],
            properties: [],
            supplier_margin_rate: supplier_margin_rate
        }).then(res => res.data.message);
    }

    deleteProduct(product) {
        return AuthService.apiDelete(this.url + product.id + "/").then(res => res.data.message);
    }

    updateProductIsActive(id, is_active){
        if (is_active)
            return AuthService.apiPut(this.url + id+'/deactivate/').then(res => res.data.message);
        else
            return AuthService.apiPut(this.url + id+'/activate/').then(res => res.data.message);
    }

    updateAllProductsIsActive(data, is_active){
        if (is_active)
            return AuthService.apiPut(this.url + 'deactivate_all/', data).then(res => res.data.message);
        else
            return AuthService.apiPut(this.url + 'activate_all/', data).then(res => res.data.message);
    }

    removeAllProducts(data){
        return AuthService.apiPut(this.url + 'remove_all/', data).then(res => res.data.message);
    }

    getCategories(campaign_id)
    {
        return AuthService.apiGet(this.fields_url, {
            params: {field_name: 'category', campaign_id: campaign_id
            }}).then(res => res.data.categorylist);
    }
    getBrands(campaign_id)
    {
        return AuthService.apiGet(this.fields_url, {
            params: {field_name: 'brand', campaign_id: campaign_id
            }}).then(res => res.data.brandlist);
    }

    addCompetitor(id, competitors){
        return AuthService.apiPost(this.url + id + "/competitors/add/", {"competitors": competitors}).then(res => res.data.data);
    }

    addCompetitorFromSuggestions(id, competitors){
        return AuthService.apiPost(this.url + id + "/competitors/add_all_from_suggestions/", {"competitors": competitors}).then(res => res.data.data);
    }

    addSupplierFromSuggestions(id, suppliers){
        return AuthService.apiPost(this.url + id + "/suppliers/add_all_from_suggestions/", {"suppliers": suppliers}).then(res => res.data.data);
    }

    // suppliers/add accepts a source PARENT product url, never a seller's own
    // url -- the created row starts with no known price and the response
    // carries the scraping request that will populate it (or null).
    addSupplier(id, product_url){
        return AuthService.apiPost(this.url + id + "/suppliers/add/", buildAddSupplierPayload(product_url)).then(res => res.data.data);
    }

    deleteSupplier(id, product_urls){
        return AuthService.apiPost(this.url + id + "/suppliers/delete/", buildSupplierProductUrlsPayload(product_urls)).then(res => res.data.message);
    }

    updateSupplierIsActive(id, product_urls, is_active){
        if (is_active)
            return AuthService.apiPut(this.url + id + '/suppliers/deactivate/', buildSupplierProductUrlsPayload(product_urls)).then(res => res.data.message);
        else
            return AuthService.apiPut(this.url + id + '/suppliers/activate/', buildSupplierProductUrlsPayload(product_urls)).then(res => res.data.message);
    }

    updateSupplierFields(id, data){
        return AuthService.apiPut(this.url + id + "/suppliers/update_fields/", buildUpdateSupplierFieldsPayload(data)).then(res => res.data.data);
    }

    addSupplierPrice(id, data){
        return AuthService.apiPost(this.url + id + "/suppliers/add_price/", buildAddSupplierPricePayload(data)).then(res => res.data.data);
    }

    deleteSupplierPrice(id, data){
        return AuthService.apiPut(this.url + id + "/suppliers/delete_price/", buildDeleteSupplierPricePayload(data.competitor_url, data.created_at)).then(res => res.data.data);
    }

    // Always creates a fresh, product-scoped scraping request -- works
    // identically for every source, whatever created it (manual add or an
    // approved supplier suggestion, which never gets a request of its own).
    checkSupplierPrice(id){
        return AuthService.apiPost(this.url + id + "/suppliers/check_price/", buildCheckSupplierPricePayload()).then(res => res.data.data);
    }

    // The product's own most recent suppliers/check_price request (or null if
    // it was never checked from this product's page) -- backs the control's
    // displayed state on tab-open and while polling.
    getLastSupplierCheck(id){
        return AuthService.apiGet(this.url + id + "/suppliers/last_check_price/").then(res => res.data.data);
    }

    deleteCompetitor(id, product_urls){
        return AuthService.apiPost(this.url + id + "/competitors/delete/", {"product_urls": product_urls}).then(res => res.data.message);
    }

    updateCompetitorIsActive(id, product_urls, is_active){
        if (is_active)
            return AuthService.apiPut(this.url + id + '/competitors/deactivate/', {product_urls: product_urls}).then(res => res.data.message);
        else
            return AuthService.apiPut(this.url + id + '/competitors/activate/',{product_urls: product_urls}).then(res => res.data.message);
    }

    updateCompetitorIsDynamicPricingActive(id, product_urls, is_dynamic_pricing_active){
        if (is_dynamic_pricing_active)
            return AuthService.apiPut(this.url + id + '/competitors/deactivate_dynamic_pricing/', {product_urls: product_urls}).then(res => res.data.message);
        else
            return AuthService.apiPut(this.url + id + '/competitors/activate_dynamic_pricing/',{product_urls: product_urls}).then(res => res.data.message);
    }

    searchCompetitors(id){
        return AuthService.apiPost(this.url + id + "/competitors/search/", "").then(res => res.data.scraping_request);
    }

    ignoreCompetitor(id, competitor_suggestions){
        return AuthService.apiPut(this.url + id + "/competitor_suggestions/not_match/", {"competitor_suggestions": competitor_suggestions}).then(res => res.data.message);
    }

    ignoreSupplierSuggestion(id, supplier_suggestions){
        return AuthService.apiPut(this.url + id + "/supplier_suggestions/not_match/", {"supplier_suggestions": supplier_suggestions}).then(res => res.data.message);
    }

    savePricingStrategy(id, pricing_strategy, keepTemplate = false){

        pricing_strategy.min_value = parseFloat(pricing_strategy.min_value);
        pricing_strategy.reduction_value = parseFloat(pricing_strategy.reduction_value);

        return AuthService.apiPut(this.url + id + "/pricing_strategy/", {"pricing_strategy" : pricing_strategy, "keep_template": keepTemplate}).then(res => res.data);
    }

    // Pause / resume the AUTOMATIC price pushes for one product. Deliberately
    // separate from savePricingStrategy: the product keeps being priced by
    // whatever rule already governs it (campaign, template or its own), so
    // resuming restores the previous behaviour with no strategy change.
    setAutoUpdatePaused(id, is_auto_update_paused){
        return AuthService.apiPut(this.url + id + "/auto_update/", {"is_auto_update_paused": is_auto_update_paused}).then(res => res.data);
    }

    applyPriceSuggestion(id)
    {
        return AuthService.apiPut(this.url + id + "/pricing_suggestion/", "").then(res => res.data.message);
    }

    getAppliedPriceSuggestions(product_id, start_date, end_date)
    {
        return AuthService.apiGet(this.url + product_id + "/applied_pricing_suggestions/",{
            params: {
                start_date: moment(start_date).startOf('day').toDate(),
                end_date: moment(end_date).endOf('day').toDate()}
        }).then(res => res.data.data);
    }

    savePriceViolation(id, violation_strategy)
    {
        violation_strategy.min_value = parseFloat(violation_strategy.min_value);
        violation_strategy.max_value = parseFloat(violation_strategy.max_value);
        return AuthService.apiPut(this.url + id + "/violation_strategy/", {"violation_strategy" : violation_strategy}).then(res => res.data.data);
    }

    autoMatchProductSuggestions(id, match_method, min_match_score)
    {
        return AuthService.apiPut(this.url + id + "/competitors/match/", {"match_method":match_method, "min_match_score":min_match_score}).then(res => res.data.message);
    }

    // target: 'competitors' (default, today's behaviour), 'suppliers', or 'both'
    // -- only meaningful while the campaign's supplier module is active.
    autoMatchAllSuggestions(campaign_id, match_method, min_match_score, target = 'competitors')
    {
        return AuthService.apiPut(this.url + "competitors/match/", {"campaign_id": campaign_id, "match_method":match_method, "min_match_score":min_match_score, "target": target}).then(res => res.data.message);
    }

    moveProducts(data)
    {
        return AuthService.apiPut(this.url + "change_campaign/", data).then(res => res.data.message);
    }

    matchCompetitorVariant(id, competitor_url, variant){
        return AuthService.apiPut(this.url + id + "/competitors/match_variant/", {"variant": variant,"competitor_url":competitor_url}).then(res => res.data.data);
    }

    updateCompetitorFields(id, data){
        return AuthService.apiPut(this.url + id + "/competitors/update_fields/", data).then(res => res.data.data);
    }

    addCompetitorPrice(id, data){
        return AuthService.apiPost(this.url + id + "/competitors/add_price/", data).then(res => res.data.data);
    }

    deleteCompetitorPrice(id, data){
        return AuthService.apiPut(this.url + id + "/competitors/delete_price/", data).then(res => res.data.data);
    }

    addCompetitorViolationAction(id, competitor_url, violation_action){
        return AuthService.apiPut(this.url + id + "/competitors/add_violation_action/", {"violation_action": violation_action, "competitor_url": competitor_url}).then(res => res.data.data);
    }

    refreshImageUrls(campaign_id) {
        return AuthService.apiPut(this.url + "refresh_image_urls/", {"campaign_id": campaign_id}).then(res => res.data.data);
    }

    refreshProductNames(campaign_id) {
        return AuthService.apiPut(this.url + "refresh_product_names/", {"campaign_id": campaign_id}).then(res => res.data.data);
    }

    mergeProducts(productsToMerge) {
        let main_product_id =  productsToMerge.filter(p=> p.keepAsMine).map(p=>p.id)[0];
        let add_as_competitor_ids =  productsToMerge.filter(p=> p.keepAsMine === false).map(p=>p.id);
        return AuthService.apiPut(this.url + "merge_products/", {"main_product_id": main_product_id, "add_as_competitor_ids": add_as_competitor_ids}).then(res => res.data.data);
    }
}
